import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { governanceAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Scales, Plus, CalendarBlank, FileText, Gavel, Users,
  Pencil, Trash, Eye, Check, X, Clock, CheckCircle, Warning
} from '@phosphor-icons/react';

const ITEM_TYPES = [
  { value: 'meeting', label: 'Meeting', icon: Users, color: 'bg-blue-100 text-blue-700' },
  { value: 'resolution', label: 'Resolution', icon: Gavel, color: 'bg-purple-100 text-purple-700' },
  { value: 'minutes', label: 'Minutes', icon: FileText, color: 'bg-green-100 text-green-700' },
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  { value: 'scheduled', label: 'Scheduled', color: 'bg-blue-100 text-blue-700' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-700' },
  { value: 'approved', label: 'Approved', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700' },
];

const GovernancePage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const [formData, setFormData] = useState({
    item_type: 'meeting',
    title: '',
    description: '',
    date: '',
    status: 'draft',
    content: '',
    votes: []
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await governanceAPI.getAll();
      setItems(response.data || []);
    } catch (error) {
      toast.error('Failed to load governance items');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    try {
      if (editingItem) {
        await governanceAPI.update(editingItem.id, formData);
        toast.success('Item updated successfully');
      } else {
        await governanceAPI.create(formData);
        toast.success('Item created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchItems();
    } catch (error) {
      toast.error('Failed to save item');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await governanceAPI.delete(id);
      toast.success('Item deleted successfully');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      item_type: item.item_type || 'meeting',
      title: item.title || '',
      description: item.description || '',
      date: item.date || '',
      status: item.status || 'draft',
      content: item.content || '',
      votes: item.votes || []
    });
    setShowModal(true);
  };

  const handleView = (item) => {
    setViewingItem(item);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      item_type: 'meeting',
      title: '',
      description: '',
      date: '',
      status: 'draft',
      content: '',
      votes: []
    });
  };

  const openNewModal = (type = 'meeting') => {
    resetForm();
    setFormData(prev => ({ ...prev, item_type: type }));
    setShowModal(true);
  };

  const getTypeConfig = (type) => {
    return ITEM_TYPES.find(t => t.value === type) || ITEM_TYPES[0];
  };

  const getStatusConfig = (status) => {
    return STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  };

  const filteredItems = items.filter(item => {
    if (filterType !== 'all' && item.item_type !== filterType) return false;
    if (filterStatus !== 'all' && item.status !== filterStatus) return false;
    return true;
  });

  const stats = {
    total: items.length,
    meetings: items.filter(i => i.item_type === 'meeting').length,
    resolutions: items.filter(i => i.item_type === 'resolution').length,
    minutes: items.filter(i => i.item_type === 'minutes').length,
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="governance-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <Scales size={32} className="text-[#7B1818]" />
            <div>
              <h1 className="font-heading text-2xl text-[#1A1614]">Governance</h1>
              <p className="text-sm text-[#8A847A]">Meetings, resolutions, and minutes</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => openNewModal('meeting')}
              variant="outline"
              className="border-[#D8D2C4]"
              data-testid="new-meeting-btn"
            >
              <Users size={18} className="mr-2" />
              New Meeting
            </Button>
            <Button
              onClick={() => openNewModal('resolution')}
              variant="outline"
              className="border-[#D8D2C4]"
              data-testid="new-resolution-btn"
            >
              <Gavel size={18} className="mr-2" />
              New Resolution
            </Button>
            <Button
              onClick={() => openNewModal('minutes')}
              className="bg-[#7B1818] hover:bg-[#5A1010] text-white"
              data-testid="new-minutes-btn"
            >
              <Plus size={18} className="mr-2" />
              New Minutes
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white border border-[#D8D2C4] p-4 rounded">
            <p className="text-sm text-[#8A847A]">Total Items</p>
            <p className="font-heading text-2xl text-[#1A1614]">{stats.total}</p>
          </div>
          <div className="bg-white border border-[#D8D2C4] p-4 rounded">
            <div className="flex items-center gap-2">
              <Users size={16} className="text-blue-600" />
              <p className="text-sm text-[#8A847A]">Meetings</p>
            </div>
            <p className="font-heading text-2xl text-[#1A1614]">{stats.meetings}</p>
          </div>
          <div className="bg-white border border-[#D8D2C4] p-4 rounded">
            <div className="flex items-center gap-2">
              <Gavel size={16} className="text-purple-600" />
              <p className="text-sm text-[#8A847A]">Resolutions</p>
            </div>
            <p className="font-heading text-2xl text-[#1A1614]">{stats.resolutions}</p>
          </div>
          <div className="bg-white border border-[#D8D2C4] p-4 rounded">
            <div className="flex items-center gap-2">
              <FileText size={16} className="text-green-600" />
              <p className="text-sm text-[#8A847A]">Minutes</p>
            </div>
            <p className="font-heading text-2xl text-[#1A1614]">{stats.minutes}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 bg-white border border-[#D8D2C4] p-4 rounded">
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#8A847A]">Type:</span>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[140px]" data-testid="filter-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {ITEM_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#8A847A]">Status:</span>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[140px]" data-testid="filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUS_OPTIONS.map(status => (
                  <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Items List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" />
          </div>
        ) : filteredItems.length > 0 ? (
          <div className="space-y-4">
            {filteredItems.map(item => {
              const typeConfig = getTypeConfig(item.item_type);
              const statusConfig = getStatusConfig(item.status);
              const TypeIcon = typeConfig.icon;
              
              return (
                <div
                  key={item.id}
                  className="bg-white border border-[#D8D2C4] p-5 rounded hover:border-[#C5A059] transition-colors"
                  data-testid={`governance-item-${item.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded ${typeConfig.color}`}>
                        <TypeIcon size={24} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs px-2 py-0.5 rounded ${typeConfig.color}`}>
                            {typeConfig.label}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded ${statusConfig.color}`}>
                            {statusConfig.label}
                          </span>
                        </div>
                        <h3 className="font-heading text-lg text-[#1A1614]">{item.title}</h3>
                        {item.description && (
                          <p className="text-sm text-[#4A453E] mt-1 line-clamp-2">{item.description}</p>
                        )}
                        {item.date && (
                          <p className="text-xs text-[#8A847A] mt-2 flex items-center gap-1">
                            <CalendarBlank size={14} />
                            {new Date(item.date).toLocaleDateString('en-US', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleView(item)}
                        className="text-[#8A847A] hover:text-[#1A1614]"
                        data-testid={`view-item-${item.id}`}
                      >
                        <Eye size={18} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(item)}
                        className="text-[#8A847A] hover:text-[#1A1614]"
                        data-testid={`edit-item-${item.id}`}
                      >
                        <Pencil size={18} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(item.id)}
                        className="text-[#8A847A] hover:text-red-600"
                        data-testid={`delete-item-${item.id}`}
                      >
                        <Trash size={18} />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white border border-[#D8D2C4] rounded p-12 text-center">
            <Scales size={48} className="mx-auto text-[#8A847A] mb-4" />
            <h3 className="font-heading text-lg text-[#1A1614] mb-2">No Governance Items Yet</h3>
            <p className="text-sm text-[#8A847A] mb-4">
              Create meetings, resolutions, or minutes to track your governance activities.
            </p>
            <Button
              onClick={() => openNewModal('meeting')}
              className="bg-[#7B1818] hover:bg-[#5A1010] text-white"
            >
              <Plus size={18} className="mr-2" />
              Create First Item
            </Button>
          </div>
        )}

        {/* Create/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-2xl" data-testid="governance-modal">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editingItem ? 'Edit Item' : 'New Governance Item'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Type Selection */}
              <div>
                <label className="block text-sm font-medium text-[#4A453E] mb-2">Type</label>
                <div className="flex gap-2">
                  {ITEM_TYPES.map(type => {
                    const TypeIcon = type.icon;
                    return (
                      <button
                        key={type.value}
                        onClick={() => setFormData(prev => ({ ...prev, item_type: type.value }))}
                        className={`flex items-center gap-2 px-4 py-2 rounded border transition-colors ${
                          formData.item_type === type.value
                            ? 'border-[#7B1818] bg-[#7B1818]/5 text-[#7B1818]'
                            : 'border-[#D8D2C4] hover:border-[#C5A059]'
                        }`}
                        data-testid={`type-${type.value}`}
                      >
                        <TypeIcon size={18} />
                        <span className="text-sm">{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-[#4A453E] mb-1">Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder={`Enter ${getTypeConfig(formData.item_type).label.toLowerCase()} title`}
                  data-testid="input-title"
                />
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-[#4A453E] mb-1">Date</label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  data-testid="input-date"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-[#4A453E] mb-1">Status</label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger data-testid="input-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(status => (
                      <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-[#4A453E] mb-1">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description or summary"
                  rows={2}
                  data-testid="input-description"
                />
              </div>

              {/* Content/Details */}
              <div>
                <label className="block text-sm font-medium text-[#4A453E] mb-1">
                  {formData.item_type === 'meeting' ? 'Agenda' : 
                   formData.item_type === 'resolution' ? 'Resolution Text' : 
                   'Minutes Content'}
                </label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  placeholder={
                    formData.item_type === 'meeting' ? 'Meeting agenda items...' :
                    formData.item_type === 'resolution' ? 'Full text of the resolution...' :
                    'Meeting minutes and notes...'
                  }
                  rows={6}
                  data-testid="input-content"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-[#D8D2C4]">
                <Button
                  variant="outline"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  className="bg-[#7B1818] hover:bg-[#5A1010] text-white"
                  data-testid="submit-btn"
                >
                  {editingItem ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Modal */}
        <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
          <DialogContent className="max-w-2xl" data-testid="view-modal">
            {viewingItem && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${getTypeConfig(viewingItem.item_type).color}`}>
                      {getTypeConfig(viewingItem.item_type).label}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded ${getStatusConfig(viewingItem.status).color}`}>
                      {getStatusConfig(viewingItem.status).label}
                    </span>
                  </div>
                  <DialogTitle className="font-heading text-xl">{viewingItem.title}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  {viewingItem.date && (
                    <div className="flex items-center gap-2 text-sm text-[#8A847A]">
                      <CalendarBlank size={16} />
                      {new Date(viewingItem.date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                  )}
                  
                  {viewingItem.description && (
                    <div>
                      <h4 className="text-sm font-medium text-[#4A453E] mb-1">Description</h4>
                      <p className="text-[#4A453E]">{viewingItem.description}</p>
                    </div>
                  )}
                  
                  {viewingItem.content && (
                    <div>
                      <h4 className="text-sm font-medium text-[#4A453E] mb-1">
                        {viewingItem.item_type === 'meeting' ? 'Agenda' : 
                         viewingItem.item_type === 'resolution' ? 'Resolution Text' : 
                         'Minutes'}
                      </h4>
                      <div className="bg-[#FAF9F6] border border-[#D8D2C4] rounded p-4 whitespace-pre-wrap text-sm text-[#4A453E]">
                        {viewingItem.content}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-4 border-t border-[#D8D2C4]">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowViewModal(false);
                        handleEdit(viewingItem);
                      }}
                    >
                      <Pencil size={16} className="mr-2" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => setShowViewModal(false)}
                      className="bg-[#7B1818] hover:bg-[#5A1010] text-white"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default GovernancePage;
